import { DateTime } from 'luxon'

import { supabaseClient } from '~/server/common/supabase/client'
import { Database } from '~/server/common/supabase/database.types'
import { toKV, toKVArray, yesNo } from '~/server/common/supabase/choices'
import { getMe, nowIso } from '~/server/common/supabase/user'
import { fetchFileUploadsMulti, groupFilesByObjectId, transformFileUpload } from '~/server/common/supabase/file-upload'

import type { ConsolidatedSpace, EventSpaceList, EventSpaceListDate, EventSpaceListMembership, EventSpaceListMembershipNote, UserSpace } from '~/shared/event/event.types'
import { EventSpaceListVariantChoices, EventSpaceStatusChoices, SupportMode } from '~/shared/common/enums'
import { allFileUploadSelectFields, venueTrustSignalSelectFields, inquiryEmailSelectFields } from '~/server/common/supabase/select'

type DB = Database['public']
type EventSpaceListRow = DB['Tables']['crm_eventspacelist']['Row'] & {
  venue_group?: { name: string | null } | null
  venue_group_name?: string | null
}

function slugify(input: string): string {
  return (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
}

function groupBy<T extends Record<string, any>, K extends keyof any>(items: T[] | null | undefined, getKey: (item: T) => K | null | undefined): Map<K, T[]> {
  const map = new Map<K, T[]>()
  for (const item of items || []) {
    const key = getKey(item)
    if (key === null || typeof key === 'undefined') continue
    if (!map.has(key as K)) map.set(key as K, [])
    map.get(key as K)!.push(item)
  }
  return map
}

async function fetchSpacesTransformed(spaceIds: number[]): Promise<Map<number, any>> {
  if (!spaceIds.length) return new Map()

  const venueCuisinesJoin = `venue_cuisines:main_venue_cuisines!left(cuisinetype_id, cuisine:main_cuisinetype(*))`
  const venueTypesJoin = `venue_types:main_venue_venue_types!left(venuetype_id, venuetype:main_venuetype!venuetype_id(*))`

  const select = `
    *,
    external_links:main_externallink(*),
    venue:venue_id!inner(
      *,
      neighborhood:neighborhood_id(*),
      ownership_group:ownership_group_id(*),
      external_links:main_externallink(*),
      ${venueCuisinesJoin},
      ${venueTypesJoin},
      contact_memberships:identity_venuecontactmembership!left(id, is_active_today, venue_contact_id, contact:identity_user!identity_venuecontac_contact_id_39dab1de_fk_identity_(*), venue_contact:identity_venuecontact!venue_contact_id(*)),
      preferred_contacts:identity_customerpreferredvenuecontact!left(id, venue_contact_id, preference_type, owner_user_id, customer_id, deleted_at, venue_contact:identity_venuecontact!venue_contact_id(*)),
      trust_signals:main_venuetrustsignal!left(${venueTrustSignalSelectFields})
    )
  `

  const { data, error } = await supabaseClient.from('main_space').select(select).in('id', spaceIds)
  if (error) throw error

  const venueIds = (data || []).map((s: any) => s?.venue?.id).filter((v: unknown) => typeof v === 'number') as number[]

  // Fetch venue and space files in parallel, each scoped to their content type
  const allFiles = await fetchFileUploadsMulti([
    { objectIds: [...new Set(venueIds)], categories: ['venue_attachment', 'venue_image'], model: 'main:venue' },
    { objectIds: [...new Set(spaceIds)], categories: ['space_attachment', 'space_image'], model: 'main:space' },
  ])

  const allFilesMap = groupFilesByObjectId(allFiles)
  const venueFilesMap = allFilesMap
  const spaceFilesMap = allFilesMap

  const map = new Map<number, any>()
  for (const space of data || []) {
    const v = space.venue || {}

    const venueFileList = venueFilesMap.get(v.id) || []
    const venueAttachments = venueFileList.filter((a: any) => a.category === 'venue_attachment').map(transformFileUpload)
    const venueImages = venueFileList.filter((a: any) => a.category === 'venue_image').map(transformFileUpload)

    const spaceFileList = spaceFilesMap.get(space.id) || []
    const spaceAttachments = spaceFileList.filter((a: any) => a.category === 'space_attachment').map(transformFileUpload)
    const spaceImages = spaceFileList.filter((a: any) => a.category === 'space_image' && !a.sub_category?.includes('floorplan')).map(transformFileUpload)

    const cuisines = (v.venue_cuisines || []).map((c) => ({
      id: c.cuisine?.id,
      name: c.cuisine?.name,
      slug: c.cuisine?.slug,
      label: c.cuisine?.name,
      value: String(c.cuisine?.id ?? ''),
    }))
    const venue_types = (v.venue_types || []).map((vt) => ({
      id: vt.venuetype?.id,
      name: vt.venuetype?.name,
      slug: vt.venuetype?.slug,
      label: vt.venuetype?.name,
      value: String(vt.venuetype?.id ?? ''),
    }))

    const transformedVenue = {
      id: v.id,
      admin_status: v.admin_status,
      create_source: v.create_source,
      google_place_id: v.google_place_id,
      venue_types,
      tagline: v.tagline,
      cuisines,
      ownership_group: v.ownership_group,
      name: v.name,
      slug: v.slug,
      is_new: v?.date_opened ? DateTime.fromISO(v.date_opened).diffNow().days < 365 : false,
      year_renovated: v.year_renovated,
      date_opened: v.date_opened,
      address_1: v.address_1,
      address_2: v.address_2,
      cross_streets: v.cross_streets,
      city: v.city,
      state: v.state,
      zip_code: v.zip_code,
      display_address: v.display_address,
      longitude: v.longitude,
      latitude: v.latitude,
      neighborhood: v.neighborhood
        ? {
            id: v.neighborhood.id,
            name: v.neighborhood.name,
            label: v.neighborhood.name,
            value: String(v.neighborhood.id),
            slug: v.neighborhood.slug,
          }
        : null,
      contacts: (v.contact_memberships || []).map((c) => ({
        id: c.contact?.id,
        username: c.contact?.username,
        title: c.contact?.title,
        first_name: c.contact?.first_name,
        last_name: c.contact?.last_name,
        email: c.contact?.email,
        office_phone: c.contact?.office_phone,
        mobile_phone: c.contact?.mobile_phone,
      })),
      preferred_contacts: (v.preferred_contacts || [])
        .filter((c: any) => c.deleted_at == null && c.venue_contact != null && c.venue_contact.status !== 'invalid' && c.venue_contact.deleted_at == null)
        .map((c: any) => ({
          id: c.venue_contact.id,
          cpvcId: c.id,
          ownerUserId: c.owner_user_id ?? null,
          vcOwnerUserId: c.venue_contact.owner_user_id ?? null,
          firstName: c.venue_contact.first_name || undefined,
          lastName: c.venue_contact.last_name || undefined,
          name: `${c.venue_contact.first_name ?? ''} ${c.venue_contact.last_name ?? ''}`.trim(),
          role: c.venue_contact.title || undefined,
          email: c.venue_contact.email || undefined,
          phone: c.venue_contact.office_phone || c.venue_contact.mobile_phone || undefined,
          customerId: c.customer_id ?? null,
        })),
      venue_contacts: (() => {
        const preferredIds = new Set((v.preferred_contacts || []).filter((c: any) => c.deleted_at == null && c.venue_contact != null).map((c: any) => c.venue_contact.id))
        return (v.contact_memberships || [])
          .filter((c: any) => c.venue_contact != null && c.venue_contact.status !== 'invalid' && c.venue_contact.deleted_at == null && !preferredIds.has(c.venue_contact.id))
          .map((c: any) => ({
            id: c.venue_contact.id,
            name: `${c.venue_contact.first_name ?? ''} ${c.venue_contact.last_name ?? ''}`.trim(),
            role: c.venue_contact.title || undefined,
            email: c.venue_contact.email || undefined,
            phone: c.venue_contact.office_phone || c.venue_contact.mobile_phone || undefined,
          }))
      })(),
      display_phone: null,
      description: v.description,
      corporate_events_description: v.corporate_events_description,
      website: v.website,
      private_events_website: v.private_events_website,
      inquiry_link: v.inquiry_link,
      is_permanently_closed: !!v.permanently_closed_aware_date,
      is_temporarily_closed: !!v.temporarily_closed_aware_date,
      price_point: v.price_point,
      wheelchair_accesible: v.wheelchair_accesible,
      accessibility_description: v.accessibility_description,
      has_accessible_parking: v.has_accessible_parking,
      has_accessible_entrance: v.has_accessible_entrance,
      has_accessible_restrooms: v.has_accessible_restrooms,
      parking: v.parking,
      rating_data: v.rating_data,
      attachments: venueAttachments,
      venue_images: venueImages,
      primary_venue_image: venueImages?.[0] || null,
      has_event_kit: venueAttachments.some((a) => Array.isArray(a.sub_category) && a.sub_category.includes('event_kit')),
      has_menus: venueAttachments.some((a) => Array.isArray(a.sub_category) && a.sub_category.includes('menu')),
      instagram_handle: v.instagram_handle,
      facebook_handle: v.facebook_handle,
      twitter_handle: v.twitter_handle,
      tiktok_handle: v.tiktok_handle,
      youtube_handle: v.youtube_handle,
      snapchat_handle: v.snapchat_handle,
      food_vendor_options: v.food_vendor_options,
      food_vendor_description: v.food_vendor_description,
      has_catering_kitchen: v.has_catering_kitchen,
      beverage_vendor_options: v.beverage_vendor_options,
      michelin_rating: v.michelin_rating,
      permit_requirement_description: v.permit_requirement_description,
      load_in_description: v.load_in_description,
      storage_description: v.storage_description,
      cleaning_description: v.cleaning_description,
      furniture_description: v.furniture_description,
      noise_restriction_description: v.noise_restriction_description,
      total_square_footage: v.total_square_footage,
      guest_room_count: v.guest_room_count,
      media_vendor_options: v.media_vendor_options,
      media_vendor_description: v.media_vendor_description,
      internet_description: v.internet_description,
      external_links: v.external_links || [],
      trust_signals: v.trust_signals || [],
    }

    const transformedSpace = {
      id: space.id,
      create_source: space.create_source,
      url: `${window.location.origin}/api/space/${space.id}/`,
      venue: transformedVenue,
      name: space.name,
      slug: space.slug,
      description: space.description,
      privacy_type: toKV(space.privacy_type, {}),
      privacy_description: space.privacy_description,
      noise_privacy: toKV(space.noise_privacy, {}),
      space_types: toKVArray(space.space_types, {}),
      outdoor_space_type: toKV(space.outdoor_space_type, {}),
      windows: toKVArray(space.windows, {}),
      amenities: toKVArray(space.amenities, {}),
      excluded_amenities: toKVArray(space.excluded_amenities, {}),
      space_media: toKVArray(space.space_media, {}),
      excluded_space_media: toKVArray(space.excluded_space_media, {}),
      similarity_pos: null,
      priority_score: space.priority_score,
      priority_sort_score: space.priority_sort_score,
      standing_capacity: space.standing_capacity,
      seated_capacity: space.seated_capacity,
      square_footage: space.square_footage,
      length_feet: null,
      width_feet: null,
      height_feet: space.height_feet,
      layout_u_shape_capacity: space.layout_u_shape_capacity,
      layout_banquet_capacity: space.layout_banquet_capacity,
      layout_theater_capacity: space.layout_theater_capacity,
      layout_reception_capacity: space.layout_reception_capacity,
      layout_conference_capacity: null,
      layout_schoolroom_capacity: space.layout_schoolroom_capacity,
      wheelchair_accesible: yesNo(space.wheelchair_accesible),
      restrooms_description: space.restrooms_description,
      f_and_b_minimum: space.f_and_b_minimum,
      f_and_b_minimum_description: space.f_and_b_minimum_description,
      room_rental_fee: space.room_rental_fee,
      attachments: spaceAttachments,
      space_images: spaceImages,
      primary_space_image: spaceImages?.find((a) => a.category === 'space_image') || null,
      distance_from_point: null,
      matching_fileupload_id: null,
      matching_fileupload: null,
      external_links: space.external_links || [],
      has_floorplans: [...spaceAttachments, ...venueAttachments].some((a) => Array.isArray(a.sub_category) && a.sub_category.includes('floorplan')),
      has_event_kit: false,
      all_images: [...spaceImages, ...venueImages],
    }

    map.set(space.id as number, transformedSpace)
  }
  return map
}

async function fetchUserSpacesTransformed(userSpaceIds: number[]): Promise<Map<number, UserSpace>> {
  if (!userSpaceIds.length) return new Map()

  const select = `
    *,
    venue:venue_id!inner(
      *,
      neighborhood:neighborhood_id(*),
      ownership_group:ownership_group_id(*),
      external_links:main_externallink(*),
      trust_signals:main_venuetrustsignal!left(${venueTrustSignalSelectFields})
    )
  `
  const { data, error } = await supabaseClient.from('crm_userspace').select(select).in('id', userSpaceIds)
  if (error) throw error

  const venueIds = (data || []).map((s) => s?.venue?.id).filter((v: unknown) => typeof v === 'number') as number[]

  // Fetch venue and user space files in parallel, each scoped to their content type
  const allFiles = await fetchFileUploadsMulti([
    { objectIds: [...new Set(venueIds)], categories: ['venue_attachment', 'venue_image'], model: 'main:venue' },
    { objectIds: [...new Set(userSpaceIds)], categories: ['space_attachment', 'space_image'], model: 'crm:userspace' },
  ])

  const allFilesMap = groupFilesByObjectId(allFiles)
  const venueFilesMap = allFilesMap
  const spaceFilesMap = allFilesMap

  const map = new Map<number, UserSpace>()
  for (const space of data || []) {
    const v = space.venue || {}
    const venueFileList = venueFilesMap.get(v.id) || []
    const venueImages = venueFileList.filter((a) => a.category === 'venue_image')
    const spaceFileList = spaceFilesMap.get(space.id) || []
    const spaceImages = spaceFileList.filter((a) => a.category === 'space_image')


    const venue = {
      id: v.id,
      name: v.name,
      google_place_id: v.google_place_id,
      venue_types: [],
      cuisines: [],
      parking: [],
      ownership_group: v.ownership_group,
      address_1: v.address_1,
      address_2: v.address_2,
      cross_streets: v.cross_streets,
      city: v.city,
      state: v.state,
      zip_code: v.zip_code,
      display_address: v.display_address,
      longitude: v.longitude,
      latitude: v.latitude,
      neighborhood: v.neighborhood
        ? {
            id: v.neighborhood.id,
            name: v.neighborhood.name,
            slug: v.neighborhood.slug,
          }
        : null,
      attachments: venueFileList,
      venue_images: venueImages,
      primary_venue_image: venueImages?.[0] || null,
      external_links: v.external_links || [],
      trust_signals: v.trust_signals || [],
      michelin_rating: v.michelin_rating,
    }

      console.log("✨", venue)

    const transformed: UserSpace = {
      id: space.id,
      name: space.name,
      venue: venue as any,
      privacy_type: toKV(space.privacy_type, {}),
      noise_privacy: toKV(space.noise_privacy, {}),
      standing_capacity: space.standing_capacity ?? undefined,
      seated_capacity: space.seated_capacity ?? undefined,
      description: space.description || '',
      privacy_description: space.privacy_description || '',
      f_and_b_minimum: space.f_and_b_minimum,
      f_and_b_minimum_description: space.f_and_b_minimum_description || '',
      room_rental_fee: space.room_rental_fee,
      wheelchair_accesible: yesNo(space.wheelchair_accesible)!,
      restrooms_description: space.restrooms_description || null,
      windows: toKVArray(space.windows, {}),
      space_types: toKVArray(space.space_types, {}),
      outdoor_space_type: toKV(space.outdoor_space_type, {}),
      space_media: toKVArray(space.space_media, {}),
      excluded_space_media: toKVArray(space.excluded_space_media, {}),
      amenities: toKVArray(space.amenities, {}),
      excluded_amenities: toKVArray(space.excluded_amenities, {}),
      primary_space_image: spaceImages?.[0] || undefined,
      external_links: [],
      has_floorplans: false,
      has_event_kit: false,
    }
    map.set(space.id as number, transformed)
  }
  return map
}

async function hydrateEventSpaceList(event: EventSpaceListRow): Promise<EventSpaceList> {
  const [{ data: memberships }, { data: dates }, { data: eventEmails }] = await Promise.all([
    supabaseClient.from('crm_eventspacelistmembership').select('*').eq('event_space_list_id', event.id).is('deleted_at', null).order('order', { ascending: true }),
    supabaseClient.from('crm_eventdate').select('*').eq('event_space_list_id', event.id),
    supabaseClient.from('crm_eventemail').select('id, event_space_list_membership_id').eq('event_space_list_id', event.id).is('deleted_at', null),
  ])

  const membershipIds = (memberships || []).map((m) => m.id)
  const eventEmailIds = (eventEmails || []).map((e) => e.id)
  const [membershipDatesRes, notesRes, inquiriesRes, membershipFilesRes, filePermissionsRes, eventEmailFilesRes] = await Promise.all([
    membershipIds.length
      ? supabaseClient.from('crm_eventspacelistmembershipdate').select('*').in('event_space_list_membership_id', membershipIds)
      : Promise.resolve({
          data: [] as DB['Tables']['crm_eventspacelistmembershipdate']['Row'][],
        }),
    membershipIds.length
      ? supabaseClient.from('crm_eventspacelistmembershipnote').select('*').is('deleted_at', null).in('event_space_list_membership_id', membershipIds)
      : Promise.resolve({
          data: [] as DB['Tables']['crm_eventspacelistmembershipnote']['Row'][],
        }),
    supabaseClient.from('crm_inquiryemail').select(inquiryEmailSelectFields).eq('event_id', event.id),
    membershipIds.length
      ? supabaseClient
          .from('file_fileupload')
          .select(allFileUploadSelectFields)
          .in('object_id', membershipIds)
          .in('category', ['attachment', 'customer_event_attachment', 'presentation_photo'])
          .not('upload', 'is', null)
          .neq('upload', '')
          .is('deleted_at', null)
      : Promise.resolve({
          data: [] as any[],
        }),
    membershipIds.length
      ? supabaseClient.from('file_filepermission').select('*').in('event_space_list_membership_id', membershipIds)
      : Promise.resolve({
          data: [] as any[],
        }),
    eventEmailIds.length
      ? supabaseClient.from('file_fileupload').select(allFileUploadSelectFields).in('object_id', eventEmailIds).eq('category', 'event_email_attachment').is('deleted_at', null)
      : Promise.resolve({
          data: [] as any[],
        }),
  ])

  const membershipDates = membershipDatesRes.data || []
  const notes = notesRes.data || []
  const inquiries = inquiriesRes.data || []
  const membershipFiles = membershipFilesRes.data || []
  const filePermissions = filePermissionsRes.data || []
  const eventEmailFiles = eventEmailFilesRes.data || []

  const notesByMembership = groupBy(notes, (n) => n.event_space_list_membership_id)
  const datesByMembership = groupBy(membershipDates, (d) => d.event_space_list_membership_id)
  const filesByMembership = groupBy(membershipFiles, (f) => f.object_id)

  // Map event email files to memberships via crm_eventemail.event_space_list_membership_id
  const emailIdToMembershipId = new Map<number, number>()
  for (const email of eventEmails || []) {
    if (email.event_space_list_membership_id) {
      emailIdToMembershipId.set(email.id, email.event_space_list_membership_id)
    }
  }
  const emailFilesByMembership = new Map<number, any[]>()
  for (const file of eventEmailFiles) {
    const membershipId = emailIdToMembershipId.get(file.object_id as number)
    if (membershipId) {
      const existing = emailFilesByMembership.get(membershipId) || []
      existing.push(file)
      emailFilesByMembership.set(membershipId, existing)
    }
  }

  // Create a map of file permissions by file_upload_id for quick lookup
  const permissionsByFileId = new Map<number, any>()
  filePermissions.forEach((perm: any) => {
    // Only consider permissions with 'entity' and 'shared_link' scopes as shared
    if (perm.permission_scope && Array.isArray(perm.permission_scope) && perm.permission_scope.includes('entity') && perm.permission_scope.includes('shared_link')) {
      permissionsByFileId.set(perm.file_upload_id, perm)
    }
  })

  const spaceIds = (memberships || []).map((m) => m.space_id).filter((v): v is number => typeof v === 'number')
  const userSpaceIds = (memberships || []).map((m) => m.user_space_id).filter((v): v is number => typeof v === 'number')

  const [spaceMap, userSpaceMap] = await Promise.all([fetchSpacesTransformed(spaceIds), fetchUserSpacesTransformed(userSpaceIds)])

  const bySpaceId = groupBy(inquiries, (i) => i.space_id || undefined)
  const byUserSpaceId = groupBy(inquiries, (i) => i.user_space_id || undefined)

  const transformMembershipFile = (f: any) => {
    const permission = permissionsByFileId.get(f.id)
    const allPermissionsForFile = filePermissions.filter((p: any) => p.file_upload_id === f.id)
    const base = transformFileUpload(f)

    return {
      ...base,
      isShared: !!permission,
      permissionId: permission?.id,
      file_filepermission: allPermissionsForFile || [],
    }
  }

  const transformedMemberships: EventSpaceListMembership[] = (memberships || []).map((m) => {
    const baseSpace: any = m.space_id ? spaceMap.get(m.space_id) : m.user_space_id ? userSpaceMap.get(m.user_space_id) : null
    let consolidatedSpace: ConsolidatedSpace | null = null
    if (baseSpace) {
      const spaceImages = Array.isArray(baseSpace.space_images) ? baseSpace.space_images : []
      const venueImages = baseSpace?.venue?.venue_images && Array.isArray(baseSpace.venue.venue_images) ? baseSpace.venue.venue_images : []
      consolidatedSpace = {
        ...baseSpace,
        variant: m.space_id ? EventSpaceListVariantChoices.Space : EventSpaceListVariantChoices.UserSpace,
        space_images: spaceImages,
        attachments: [],
        all_images: [...spaceImages, ...venueImages],
        url: baseSpace.url || '',
      } as ConsolidatedSpace
    }

    const membershipNotes = (notesByMembership.get(m.id) || []).map((n) => ({
      id: n.id,
      created_at: n.created_at,
      updated_at: n.updated_at,
      create_source: n.create_source,
      update_author_id: n.update_author_id,
      note_text: n.note_text,
      event_space_list_membership: n.event_space_list_membership_id,
    })) as EventSpaceListMembershipNote[]

    const membershipInquiries = m.space_id ? bySpaceId.get(m.space_id) || [] : m.user_space_id ? byUserSpaceId.get(m.user_space_id) || [] : []

    const membershipAttachments = [
      ...(filesByMembership.get(m.id) || []).filter((f) => f.category === 'attachment' || f.category === 'customer_event_attachment').map(transformMembershipFile),
      ...(emailFilesByMembership.get(m.id) || []).map(transformMembershipFile),
    ]

    const membershipPresentationPhotos = (filesByMembership.get(m.id) || [])
      .filter((f) => f.category === 'presentation_photo' && (f.content_type_id === 69 || f.content_type_id === null || typeof f.content_type_id === 'undefined'))
      .map(transformMembershipFile)

    return {
      id: m.id,
      created_at: m.created_at,
      updated_at: m.updated_at,
      create_source: m.create_source,
      update_author_id: m.update_author_id,
      event_space_list_id: m.event_space_list_id,
      space_id: m.space_id,
      user_space_id: m.user_space_id,
      order: m.order,
      status: m.status as EventSpaceStatusChoices,
      is_shortlisted: m.is_shortlisted,
      dates: (datesByMembership.get(m.id) || []).map((md) => ({
        id: md.event_date_id,
        event_space_list_id: event.id,
        date: '',
      })),
      space: m.space_id ? spaceMap.get(m.space_id) : undefined,
      user_space: m.user_space_id ? userSpaceMap.get(m.user_space_id) : null,
      consolidatedSpace,
      notes: membershipNotes,
      attachments: membershipAttachments,
      presentation_photos: membershipPresentationPhotos,
      availability: m.availability,
      cons: m.cons,
      pros: m.pros,
      internal_notes: m.internal_notes,
      shareable_notes: m.shareable_notes,
      pricing: m.pricing,
      location: m.location,
      venue_description: m.venue_description,
      space_description: m.space_description,
      f_and_b_minimum_description: m.f_and_b_minimum_description,
      venue_website: m.venue_website,
      food_package: m.food_package,
      beverage_package: m.beverage_package,
      inquiry_emails: membershipInquiries,
    } as EventSpaceListMembership
  })

  const transformed: EventSpaceList = {
    id: event.id,
    created_at: event.created_at,
    updated_at: event.updated_at,
    create_source: event.create_source,
    update_author_id: event.update_author_id,
    user_owner_id: event.user_owner_id,
    entity_owner_id: event.entity_owner_id,
    slug: event.slug,
    name: event.name,
    criteria: event.criteria,
    criteria_json: event.criteria_json as any,
    venue_group_name: event.venue_group?.name ?? event.venue_group_name ?? null,
    venue_group_id: event.venue_group_id,
    location: event.location,
    location_max_distance: event.location_max_distance,
    latitude: event.latitude,
    longitude: event.longitude,
    seated_min_headcount: event.seated_min_headcount,
    seated_max_headcount: event.seated_max_headcount,
    standing_min_headcount: event.standing_min_headcount,
    standing_max_headcount: event.standing_max_headcount,
    space_memberships: transformedMemberships,
    event_space_list_dates: (dates || []).map((d) => ({
      id: d.id,
      start_date: d.start_date,
      end_date: d.end_date,
    })),
    display_json: (event.display_json as any) || ({} as any),
    support_mode: event.support_mode as any,
    assigned_to: event.assigned_to_id,
    assigned_at: event.assigned_at,
    event_type: event.event_type as any,
    space_type: event.space_type as any,
    budget_per_person: event.budget_per_person,
    budget_total: event.budget_total,
    beverage_service: event.beverage_service as any,
    food_package: event.food_package as any,
    amenities_requirements: event.amenities_requirements as any,
    media_requirements: event.media_requirements as any,
    privacy_requirement: event.privacy_requirement as any,
    num_attendees: event.num_attendees,
    event_format: event.event_format as any,
    date_flexible: event.date_flexible,
    date_flexible_description: event.date_flexible_description,
  }

  return transformed
}

export async function getEventSpaceListById(id: number): Promise<EventSpaceList> {
  const { data: event, error } = await supabaseClient
    .from('crm_eventspacelist')
    .select('*, venue_group:main_venuegroup!crm_eventspacelist_venue_group_id_b4862aa1_fk_main_venu(name)')
    .eq('id', id)
    .single()
  if (error) throw error
  return hydrateEventSpaceList(event)
}

export async function getEventSpaceListBySlug(slug: string): Promise<EventSpaceList> {
  const { data: event, error } = await supabaseClient
    .from('crm_eventspacelist')
    .select('*, venue_group:main_venuegroup!crm_eventspacelist_venue_group_id_b4862aa1_fk_main_venu(name)')
    .eq('slug', slug)
    .single()
  if (error) throw error
  return hydrateEventSpaceList(event)
}

export async function getEventSlugById(id: number): Promise<string> {
  const { data, error } = await supabaseClient.from('crm_eventspacelist').select('slug').eq('id', id).single()
  if (error) throw error
  return data.slug
}

export async function createEventSpaceList(payload: Partial<EventSpaceList>): Promise<EventSpaceList> {
  const me = await getMe()
  if (!me) throw new Error('Not authenticated')

  const now = new Date()
  const timeStamp = now.toISOString().slice(11, 19).replace(/:/g, '')
  const firstDate = Array.isArray(payload.event_space_list_dates) && payload.event_space_list_dates[0] ? payload.event_space_list_dates[0] : undefined
  const eventDateStr = firstDate?.start_date ? new Date(firstDate.start_date).toISOString().slice(0, 10) : now.toISOString().slice(0, 10)
  const slug = `${slugify(payload.name || 'event')}-${eventDateStr}-${timeStamp}`

  const insertData: DB['Tables']['crm_eventspacelist']['Insert'] = {
    created_at: nowIso(),
    updated_at: nowIso(),
    slug,
    name: payload.name || 'Untitled Event',
    support_mode: payload.support_mode || SupportMode.SELF_SERVICE,
    update_author_id: me.id,
    user_owner_id: me.id,
    entity_owner_id: me.active_entity_id,
    criteria: payload.criteria || null,
    criteria_json: (payload.criteria_json as any) || null,
    venue_group_id: payload.venue_group_id || null,
    location: payload.location || null,
    location_max_distance: payload.location_max_distance || null,
    latitude: payload.latitude || null,
    longitude: payload.longitude || null,
    seated_min_headcount: payload.seated_min_headcount || null,
    seated_max_headcount: payload.seated_max_headcount || null,
    standing_min_headcount: payload.standing_min_headcount || null,
    standing_max_headcount: payload.standing_max_headcount || null,
    display_json: payload.display_json || { view_mode: 'grid' },
    assigned_to_id: payload.assigned_to || null,
    assigned_at: payload.assigned_at || null,
    event_type: payload.event_type || null,
    event_format: (payload as any).event_format || null,
    space_type: (payload.space_type as any) || null,
    budget_per_person: payload.budget_per_person || null,
    budget_total: payload.budget_total || null,
    beverage_service: payload.beverage_service || null,
    food_package: payload.food_package || null,
    amenities_requirements: (payload.amenities_requirements as any) || null,
    media_requirements: (payload.media_requirements as any) || null,
    privacy_requirement: (payload.privacy_requirement as any) || null,
    num_attendees: payload.num_attendees || null,
    archived_at: null,
    deleted_at: null,
    revel_recs_opted_in: false,
    // date_flexible is NOT NULL with no DB default; events default to a fixed date.
    date_flexible: payload.date_flexible ?? false,
    date_flexible_description: payload.date_flexible_description ?? null,
  }

  const { data: created, error } = await supabaseClient.from('crm_eventspacelist').insert(insertData).select('*').single()
  if (error) throw error

  if (Array.isArray(payload.event_space_list_dates)) {
    const rows = payload.event_space_list_dates.map((d: any) => ({
      event_space_list_id: created.id,
      start_date: d.start_date ?? (d as any).date,
      end_date: d.end_date ?? null,
    })) as DB['Tables']['crm_eventdate']['Insert'][]
    if (rows.length) await supabaseClient.from('crm_eventdate').insert(rows)
  }

  return await getEventSpaceListById(created.id)
}

export async function updateEventSpaceList(id: number, partial: Partial<EventSpaceList>): Promise<EventSpaceList> {
  // Extract fields that are not columns in crm_eventspacelist table
  // Owner fields are immutable after creation and must never be updated here.
  const { event_space_list_dates, space_memberships, user_owner_id, entity_owner_id, ...updateData } = partial as any
  void user_owner_id
  void entity_owner_id

  const { data: updated, error } = await supabaseClient
    .from('crm_eventspacelist')
    .update({ ...updateData, updated_at: nowIso() })
    .eq('id', id)
    .is('deleted_at', null) // Only update non-deleted records
    .select('*')
    .limit(1)
    .single()
  if (error) throw error

  // Only process dates if explicitly provided (not undefined)
  // This prevents accidental deletion when updating other fields
  if (event_space_list_dates !== undefined) {
    const incoming = (event_space_list_dates || []) as EventSpaceListDate[]
    const { data: existingDates } = await supabaseClient.from('crm_eventdate').select('id').eq('event_space_list_id', id)

    const incomingWithIds = incoming.filter((d) => Number.isFinite(d.id))
    const keepIds = new Set((incomingWithIds as any[]).map((d: any) => (d as any).id))

    for (const d of incomingWithIds as any[]) {
      await supabaseClient
        .from('crm_eventdate')
        .update({
          start_date: d.start_date,
          end_date: d.end_date,
        })
        .eq('id', d.id)
    }
    const newDates = (incoming as any[])
      .filter((d) => !Number.isFinite(d.id))
      .map((d) => ({
        event_space_list_id: id,
        start_date: d.start_date ?? d.date,
        end_date: d.end_date ?? null,
      })) as DB['Tables']['crm_eventdate']['Insert'][]
    if (newDates.length) {
      await supabaseClient.from('crm_eventdate').insert(newDates)
    }

    const toDelete = (existingDates || []).filter((ed) => !keepIds.has(ed.id))
    if (toDelete.length) {
      await supabaseClient
        .from('crm_eventdate')
        .delete()
        .in(
          'id',
          toDelete.map((d) => d.id)
        )
    }
  }

  return await getEventSpaceListById(updated.id)
}

export async function softDeleteEventSpaceList(id: number): Promise<void> {
  await supabaseClient
    .from('crm_eventspacelist')
    .update({
      deleted_at: nowIso(),
      updated_at: nowIso(),
    })
    .eq('id', id)
}

export async function addSpaceMembership(params: {
  event_space_list_id: number
  space_id?: number | null
  user_space_id?: number | null
  order?: number
  is_shortlisted?: boolean
}): Promise<{ membership: EventSpaceListMembership; alreadyExists: boolean }> {
  const { event_space_list_id, space_id = null, user_space_id = null } = params
  if (!event_space_list_id) throw new Error('event_space_list_id is required')
  if ((space_id && user_space_id) || (!space_id && !user_space_id)) {
    throw new Error('Provide either space_id or user_space_id')
  }

  // Check for existing membership to prevent duplicates
  const existingQuery = supabaseClient.from('crm_eventspacelistmembership').select('*').eq('event_space_list_id', event_space_list_id).is('deleted_at', null)

  if (space_id) {
    existingQuery.eq('space_id', space_id)
  } else if (user_space_id) {
    existingQuery.eq('user_space_id', user_space_id)
  }

  const { data: existing } = await existingQuery.maybeSingle()

  // If membership already exists, return it with a flag
  if (existing) {
    return { membership: existing as any, alreadyExists: true }
  }

  // Fetch venue and space data to populate descriptions
  let venue_website: string | null = null
  let venue_description: string | null = null
  let space_description: string | null = null

  if (space_id) {
    // Fetch main_space and related venue
    const { data: spaceData } = await supabaseClient.from('main_space').select('description, venue:venue_id!inner(website, description)').eq('id', space_id).single()

    if (spaceData) {
      space_description = spaceData.description
      if (spaceData.venue) {
        venue_website = (spaceData.venue as any).website
        venue_description = (spaceData.venue as any).description
      }
    }
  } else if (user_space_id) {
    // Fetch crm_userspace and related venue
    const { data: userSpaceData } = await supabaseClient.from('crm_userspace').select('description, venue:venue_id!inner(website, description)').eq('id', user_space_id).single()

    if (userSpaceData) {
      space_description = userSpaceData.description
      if (userSpaceData.venue) {
        venue_website = (userSpaceData.venue as any).website
        venue_description = (userSpaceData.venue as any).description
      }
    }
  }

  const row: DB['Tables']['crm_eventspacelistmembership']['Insert'] = {
    created_at: nowIso(),
    updated_at: nowIso(),
    event_space_list_id,
    space_id: space_id,
    user_space_id: user_space_id,
    order: typeof params.order === 'number' ? params.order : 0,
    is_shortlisted: !!params.is_shortlisted,
    status: EventSpaceStatusChoices.NEW,
    create_source: null,
    update_author_id: null,
    availability: null,
    cons: null,
    internal_notes: null,
    shareable_notes: null,
    location: null,
    pricing: null,
    pros: null,
    custom_fields_json: null,
    venue_website,
    venue_description,
    space_description,
  }

  console.log('[addSpaceMembership] adding row', row)

  const { data, error } = await supabaseClient.from('crm_eventspacelistmembership').insert(row).select('*').single()
  if (error) throw error

  return { membership: data as any, alreadyExists: false }
}

export async function updateSpaceMembership(id: number, partial: Partial<EventSpaceListMembership>): Promise<EventSpaceListMembership> {
  const { data, error } = await supabaseClient
    .from('crm_eventspacelistmembership')
    .update({ ...(partial as any), updated_at: nowIso() })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data as any
}

export async function removeSpaceMembership(id: number): Promise<void> {
  const { error } = await supabaseClient.from('crm_eventspacelistmembership').update({ deleted_at: nowIso(), updated_at: nowIso() }).eq('id', id)
  if (error) throw error
}

export async function addSpaceMembershipNote(membershipId: number, noteText: string): Promise<EventSpaceListMembershipNote> {
  const row: DB['Tables']['crm_eventspacelistmembershipnote']['Insert'] = {
    created_at: nowIso(),
    updated_at: nowIso(),
    deleted_at: null,
    event_space_list_membership_id: membershipId,
    note_text: noteText,
    update_author_id: null,
    create_source: null,
    id: undefined,
  }
  const { data, error } = await supabaseClient.from('crm_eventspacelistmembershipnote').insert(row).select('*').single()
  if (error) throw error
  return {
    id: data.id,
    created_at: data.created_at,
    updated_at: data.updated_at,
    create_source: data.create_source,
    update_author_id: data.update_author_id,
    note_text: data.note_text,
    event_space_list_membership: data.event_space_list_membership_id,
  } as EventSpaceListMembershipNote
}

export async function updateSpaceMembershipNote(noteId: number, noteText: string): Promise<EventSpaceListMembershipNote> {
  const { data, error } = await supabaseClient.from('crm_eventspacelistmembershipnote').update({ note_text: noteText, updated_at: nowIso() }).eq('id', noteId).select('*').single()
  if (error) throw error
  return {
    id: data.id,
    created_at: data.created_at,
    updated_at: data.updated_at,
    create_source: data.create_source,
    update_author_id: data.update_author_id,
    note_text: data.note_text,
    event_space_list_membership: data.event_space_list_membership_id,
  } as EventSpaceListMembershipNote
}

export async function softDeleteSpaceMembershipNote(noteId: number): Promise<void> {
  const { error } = await supabaseClient.from('crm_eventspacelistmembershipnote').update({ deleted_at: nowIso(), updated_at: nowIso() }).eq('id', noteId)
  if (error) throw error
}

export async function listMyInquiries(params: { venue?: number; limit?: number } = {}) {
  const me = await getMe()
  if (!me) throw new Error('Not authenticated')
  let q = supabaseClient.from('crm_inquiryemail').select(inquiryEmailSelectFields).eq('user_id', me.id).order('created_at', { ascending: false })
  if (typeof params.venue === 'number') q = q.eq('venue_id', params.venue)
  if (typeof params.limit === 'number') q = q.limit(params.limit)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function bulkCreateInquiries(payload: {
  inquiries: Array<{
    event_id: number
    space_id?: number | null
    user_space_id?: number | null
    venue_id: number
    user_message_for_venue?: string | null
  }>
}): Promise<any[]> {
  const me = await getMe()
  if (!me) throw new Error('Not authenticated')
  const rows: DB['Tables']['crm_inquiryemail']['Insert'][] = payload.inquiries.map((inq) => ({
    created_at: nowIso(),
    updated_at: nowIso(),
    user_id: me.id,
    event_id: inq.event_id,
    space_id: inq.space_id ?? null,
    user_space_id: inq.user_space_id ?? null,
    venue_id: inq.venue_id,
    user_message_for_venue: inq.user_message_for_venue ?? null,
    delivery_method: null,
    delivery_metadata_json: null,
    delivery_content_json: null,
    message_body: null,
    data: null,
    id: undefined,
    update_author_id: null,
    sent_at: null,
    create_source: null,
  }))
  const { data: created, error } = await supabaseClient.from('crm_inquiryemail').insert(rows).select(inquiryEmailSelectFields)
  if (error) throw error

  for (const inq of created || []) {
    const upsertRow: Partial<DB['Tables']['crm_eventspacelistmembership']['Insert']> = {
      created_at: nowIso(),
      updated_at: nowIso(),
      event_space_list_id: inq.event_id!,
      space_id: inq.space_id,
      user_space_id: inq.user_space_id,
      order: 0,
      is_shortlisted: false,
      status: EventSpaceStatusChoices.CONTACTED,
    }
    await supabaseClient.from('crm_eventspacelistmembership').upsert(upsertRow as any, {
      onConflict: 'event_space_list_id,space_id,user_space_id',
      ignoreDuplicates: false,
    })
  }

  return created || []
}

export async function createUserSpaceAndAttach(params: {
  venue_id: number
  name: string
  event_space_list_id?: number
  standing_capacity?: number | null
  seated_capacity?: number | null
  space_types?: string[] | null
}): Promise<{ userSpaceId: number }> {
  const { data: us, error } = await supabaseClient
    .from('crm_userspace')
    .insert({
      created_at: nowIso(),
      updated_at: nowIso(),
      venue_id: params.venue_id,
      name: params.name,
      data: null,
      description: null,
      excluded_amenities: null,
      excluded_space_media: null,
      f_and_b_minimum: null,
      f_and_b_minimum_description: null,
      height_feet: null,
      layout_banquet_capacity: null,
      layout_conference_capacity: null,
      layout_reception_capacity: null,
      layout_schoolroom_capacity: null,
      layout_theater_capacity: null,
      layout_u_shape_capacity: null,
      length_feet: null,
      noise_privacy: null,
      outdoor_space_type: null,
      priority_score: null,
      priority_score_override: null,
      priority_sort_score: null,
      privacy_description: null,
      privacy_type: null,
      restrooms_description: null,
      room_rental_fee: null,
      seated_capacity: params.seated_capacity ?? null,
      standing_capacity: params.standing_capacity ?? null,
      source_map: null,
      space_media: null,
      space_types: params.space_types ?? null,
      square_footage: null,
      update_author_id: null,
      wheelchair_accesible: null,
      width_feet: null,
      windows: null,
      completeness_score: null,
      create_source: null,
    } as DB['Tables']['crm_userspace']['Insert'])
    .select('id')
    .single()
  if (error) throw error

  if (params.event_space_list_id) {
    await supabaseClient.from('crm_eventspacelistmembership').insert({
      created_at: nowIso(),
      updated_at: nowIso(),
      event_space_list_id: params.event_space_list_id,
      user_space_id: us.id,
      order: 0,
      is_shortlisted: false,
      status: EventSpaceStatusChoices.NEW,
    } as DB['Tables']['crm_eventspacelistmembership']['Insert'])
  }

  return { userSpaceId: us.id }
}
